// Used by: LandingAgent (AnswerCard), WorkspaceAgent (WorkspaceTurn).
import React from 'react';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TroubleshootIcon from '@mui/icons-material/TroubleshootOutlined';
import { API_BASE_URL } from '../../constants';
import { useSupportRef } from '../../support/SupportRef';

// Same-origin only, by design. The debug UI is a gated /api/* page, and a
// document navigation carries cookies but not the Clerk bearer token — so
// pointing this at a different API origin would 401 rather than open. Every
// deployment we have is same-origin behind Caddy (quasar-deploy.sh pins both
// vars below to ""), which is why the bases are read but not relied on. A
// genuinely split-origin setup needs an authenticated handoff, not a link.
//
// Both names are still read, because the repo has two and they disagree: the
// axios client reads REACT_APP_API_BASE_URL, the agent slices read
// REACT_APP_API_BASE. Honouring both means a same-host-different-port setup
// works and nothing silently points at the frontend. Last resort is the CRA
// dev server, whose proxy does not forward document navigations.
//
// Read per render, not once at module load: CRA inlines each
// `process.env.REACT_APP_*` site at build time either way, so this costs
// nothing in production and keeps the component testable without a second
// module registry (which would drag in a second React).
function debugUiBase(): string {
  return (
    API_BASE_URL
    || process.env.REACT_APP_API_BASE
    || (process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : '')
  );
}

// Whether the affordance appears at all. The security boundary is server-side
// and unaffected by this — /api/debug/* is account-scoped, so no one reaches
// another account's trace with or without a link. What this decides is whether
// a raw prompt/cost/token browser sits one click from an end user's answer,
// which is an operator's call, not a permission. Deliberately not gated on
// Clerk's org:admin: that role means workspace administration, not "debugs
// prompts", and it is null in a personal workspace. Default on; set
// REACT_APP_SHOW_TRACE_LINKS=false to hide it for a deployment.
// Only that exact value hides it, so a typo fails visible rather than dark.
function traceLinksEnabled(): boolean {
  return (process.env.REACT_APP_SHOW_TRACE_LINKS || '').toLowerCase() !== 'false';
}

/** The Debug UI URL for a session, or for a whole thread's roll-up (`?thread=`). */
export function traceUrl(query: { session?: string | null; thread?: string | null }): string {
  const qs = query.session ? `session=${encodeURIComponent(query.session)}` : `thread=${encodeURIComponent(query.thread || '')}`;
  return `${debugUiBase()}/api/debug/ui?${qs}`;
}

export function traceLinksOn(): boolean { return traceLinksEnabled(); }

export default function TraceLink({ sessionId, label = 'Trace', title = 'Open the agent trace behind this answer' }: { sessionId: string; label?: string; title?: string }) {
  useSupportRef('session', sessionId);   // on screen while mounted, so a support link reaches it — shown or not
  if (!traceLinksEnabled()) return null;
  return (
    <Tooltip title={title} arrow>
      <Button
        size="small"
        variant="text"
        component="a"
        href={`${debugUiBase()}/api/debug/ui?session=${encodeURIComponent(sessionId)}`}
        target="_blank"
        rel="noopener noreferrer"
        startIcon={<TroubleshootIcon fontSize="small" />}
        sx={{ textTransform: 'none', py: 0.25, color: 'text.secondary' }}
      >
        {label}
      </Button>
    </Tooltip>
  );
}
